import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  PhotoIcon, 
  CalendarIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { useQuery } from 'react-query';
import axios from 'axios';

const BlogPostForm = ({ post, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    tags: [],
    status: 'draft',
    featured_image: null,
    featured_image_preview: null,
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    publish_date: '',
    publish_time: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // Fetch categories
  const { data: categoriesData } = useQuery('blogCategories', async () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    const response = await axios.get(`${apiUrl}/admin/blog-categories`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data.data;
  }, {
    onError: (error) => {
      console.error('Error fetching blog categories:', error);
    }
  });

  // Placeholder categories when API data is not available
  const placeholderCategories = [
    { id: 1, name: 'Safari' },
    { id: 2, name: 'Adventure' },
    { id: 3, name: 'Culture' },
    { id: 4, name: 'Photography' },
    { id: 5, name: 'Beaches' },
    { id: 6, name: 'Travel Tips' },
    { id: 7, name: 'Wildlife' },
    { id: 8, name: 'Food' }
  ];

  const categories = categoriesData || placeholderCategories;

  // Initialize form with post data if editing
  useEffect(() => {
    if (post) {
      const publishDate = post.published_at ? new Date(post.published_at) : null;
      
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        category: post.category || '',
        tags: post.tags || [],
        status: post.status?.toLowerCase() || 'draft',
        featured_image: null,
        featured_image_preview: post.featured_image || null,
        seo_title: post.seo_title || '',
        seo_description: post.seo_description || '',
        seo_keywords: post.seo_keywords || '',
        publish_date: publishDate ? publishDate.toISOString().split('T')[0] : '',
        publish_time: publishDate ? publishDate.toTimeString().split(' ')[0].substring(0, 5) : ''
      });
    }
  }, [post]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'title' && !post) {
      // Auto-generate slug from title for new posts
      const slug = value
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        slug
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle featured image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        featured_image: file,
        featured_image_preview: URL.createObjectURL(file)
      }));
      
      // Clear error for this field
      if (errors.featured_image) {
        setErrors(prev => ({
          ...prev,
          featured_image: null
        }));
      }
    }
  };

  // Handle tag input
  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  // Add tag
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  // Handle key press in tag input
  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Remove tag
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    }
    
    if (!formData.excerpt.trim()) {
      newErrors.excerpt = 'Excerpt is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    if (formData.status === 'scheduled' && (!formData.publish_date || !formData.publish_time)) {
      newErrors.publish_date = 'Publish date and time are required for scheduled posts';
    }
    
    if (!post && !formData.featured_image && !formData.featured_image_preview) {
      newErrors.featured_image = 'Featured image is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare form data for submission
      const postFormData = new FormData();
      postFormData.append('title', formData.title);
      postFormData.append('slug', formData.slug);
      postFormData.append('excerpt', formData.excerpt);
      postFormData.append('content', formData.content);
      postFormData.append('category', formData.category);
      postFormData.append('status', formData.status);
      postFormData.append('seo_title', formData.seo_title);
      postFormData.append('seo_description', formData.seo_description);
      postFormData.append('seo_keywords', formData.seo_keywords);
      
      // Add tags as JSON string
      postFormData.append('tags', JSON.stringify(formData.tags));
      
      // Add featured image if changed
      if (formData.featured_image) {
        postFormData.append('featured_image', formData.featured_image);
      }
      
      // Add publish date and time for scheduled posts
      if (formData.status === 'scheduled' && formData.publish_date && formData.publish_time) {
        const publishDateTime = `${formData.publish_date}T${formData.publish_time}:00`;
        postFormData.append('published_at', publishDateTime);
      }
      
      // If editing, add the method for Laravel to recognize
      if (post) {
        postFormData.append('_method', 'PUT');
      }
      
      await onSubmit(postFormData);
    } catch (error) {
      console.error('Error submitting blog post:', error);
      alert('Failed to save blog post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {post ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-gray-400 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title and Slug */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`mt-1 block w-full border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
              />
              {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
            </div>
            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className={`mt-1 block w-full border ${errors.slug ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
              />
              {errors.slug && <p className="mt-1 text-sm text-red-500">{errors.slug}</p>}
              <p className="mt-1 text-xs text-gray-500">URL-friendly name for your blog post.</p>
            </div>
          </div>
          
          {/* Excerpt */}
          <div>
            <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700">
              Excerpt <span className="text-red-500">*</span>
            </label>
            <textarea
              id="excerpt"
              name="excerpt"
              rows={3}
              value={formData.excerpt}
              onChange={handleChange}
              className={`mt-1 block w-full border ${errors.excerpt ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
              placeholder="Brief summary of the blog post"
            />
            {errors.excerpt && <p className="mt-1 text-sm text-red-500">{errors.excerpt}</p>}
            <p className="mt-1 text-xs text-gray-500">A short summary that appears in blog listings (150-200 characters recommended).</p>
          </div>
          
          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700">
              Content <span className="text-red-500">*</span>
            </label>
            <textarea
              id="content"
              name="content"
              rows={10}
              value={formData.content}
              onChange={handleChange}
              className={`mt-1 block w-full border ${errors.content ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
              placeholder="Write your blog post content here..."
            />
            {errors.content && <p className="mt-1 text-sm text-red-500">{errors.content}</p>}
            <p className="mt-1 text-xs text-gray-500">
              HTML formatting is supported. Use &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;blockquote&gt;, &lt;img&gt; tags.
            </p>
          </div>
          
          {/* Category and Status */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border ${errors.category ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md`}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
          
          {/* Publish Date and Time (for scheduled posts) */}
          {formData.status === 'scheduled' && (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="publish_date" className="block text-sm font-medium text-gray-700">
                  Publish Date <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="date"
                    id="publish_date"
                    name="publish_date"
                    value={formData.publish_date}
                    onChange={handleChange}
                    className={`block w-full pl-10 sm:text-sm border ${errors.publish_date ? 'border-red-500' : 'border-gray-300'} rounded-md py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary`}
                  />
                </div>
                {errors.publish_date && <p className="mt-1 text-sm text-red-500">{errors.publish_date}</p>}
              </div>
              <div>
                <label htmlFor="publish_time" className="block text-sm font-medium text-gray-700">
                  Publish Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  id="publish_time"
                  name="publish_time"
                  value={formData.publish_time}
                  onChange={handleChange}
                  className={`mt-1 block w-full border ${errors.publish_date ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                />
              </div>
            </div>
          )}
          
          {/* Tags */}
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
              Tags
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <div className="relative flex items-stretch flex-grow focus-within:z-10">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <TagIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  type="text"
                  id="tags"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyPress={handleTagKeyPress}
                  className="focus:ring-primary focus:border-primary block w-full rounded-none rounded-l-md pl-10 sm:text-sm border-gray-300"
                  placeholder="Add a tag"
                />
              </div>
              <button
                type="button"
                onClick={addTag}
                className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              >
                Add
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-light text-primary"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="flex-shrink-0 ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-primary hover:bg-primary hover:text-white focus:outline-none focus:bg-primary focus:text-white"
                    >
                      <span className="sr-only">Remove tag</span>
                      <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">Press Enter or click Add to add a tag.</p>
          </div>
          
          {/* Featured Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Featured Image {!post && <span className="text-red-500">*</span>}
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              {formData.featured_image_preview ? (
                <div className="space-y-1 text-center">
                  <img
                    src={formData.featured_image_preview}
                    alt="Featured"
                    className="mx-auto h-32 object-cover"
                  />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="featured_image"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                    >
                      <span>Change image</span>
                      <input
                        id="featured_image"
                        name="featured_image"
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="space-y-1 text-center">
                  <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" aria-hidden="true" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="featured_image"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                    >
                      <span>Upload a file</span>
                      <input
                        id="featured_image"
                        name="featured_image"
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleImageChange}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                </div>
              )}
            </div>
            {errors.featured_image && <p className="mt-1 text-sm text-red-500">{errors.featured_image}</p>}
          </div>
          
          {/* SEO Section */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">SEO Settings</h3>
            <div className="space-y-6">
              <div>
                <label htmlFor="seo_title" className="block text-sm font-medium text-gray-700">
                  SEO Title
                </label>
                <input
                  type="text"
                  id="seo_title"
                  name="seo_title"
                  value={formData.seo_title}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="SEO optimized title (if different from post title)"
                />
                <p className="mt-1 text-xs text-gray-500">If left empty, the post title will be used.</p>
              </div>
              <div>
                <label htmlFor="seo_description" className="block text-sm font-medium text-gray-700">
                  Meta Description
                </label>
                <textarea
                  id="seo_description"
                  name="seo_description"
                  rows={2}
                  value={formData.seo_description}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Brief description for search engines"
                />
                <p className="mt-1 text-xs text-gray-500">Recommended length: 150-160 characters.</p>
              </div>
              <div>
                <label htmlFor="seo_keywords" className="block text-sm font-medium text-gray-700">
                  Meta Keywords
                </label>
                <input
                  type="text"
                  id="seo_keywords"
                  name="seo_keywords"
                  value={formData.seo_keywords}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Comma-separated keywords"
                />
              </div>
            </div>
          </div>
          
          {/* Form Actions */}
          <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {isSubmitting ? 'Saving...' : post ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogPostForm;
